import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { api } from '../api/client.js'
import { useAuth } from '../auth.jsx'

const TOPICS = ['question', 'spot', 'sponsor', 'bug', 'other']
const inputClass =
  'rounded-xl border-[1.5px] border-edge bg-surface px-4 text-base font-medium text-ink outline-none focus:border-teal'

/** Contact form: the reply goes to the email the person leaves here. */
export default function ContactPage() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const [params] = useSearchParams()
  const [topic, setTopic] = useState(TOPICS.includes(params.get('topic')) ? params.get('topic') : 'question')
  const [email, setEmail] = useState(user?.email && !user.email.endsWith('@dev.local') ? user.email : '')
  const [name, setName] = useState('')
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('') // honeypot, hidden from people
  const [state, setState] = useState({ busy: false, error: null, sent: false })

  const submit = async (e) => {
    e.preventDefault()
    if (message.trim().length < 10) return setState({ busy: false, error: t('contact.tooShort'), sent: false })
    setState({ busy: true, error: null, sent: false })
    try {
      await api.contact({
        email: email.trim(),
        name: name.trim() || null,
        topic,
        message: message.trim(),
        locale: i18n.resolvedLanguage,
        platform: 'web',
        feeder_slug: params.get('feeder'),
        website: website || null,
      })
      setState({ busy: false, error: null, sent: true })
    } catch (err) {
      const text = err.status === 429 ? t('contact.tooMany') : err.status === 422 ? t('contact.badEmail') : t('contact.failed')
      setState({ busy: false, error: text, sent: false })
    }
  }

  if (state.sent) {
    return (
      <main className="mx-auto flex max-w-xl flex-col items-center gap-4 px-4 py-24 text-center sm:px-8">
        <span className="text-6xl">📬</span>
        <h1 className="text-3xl font-black">{t('contact.sentTitle')}</h1>
        <p className="text-lg text-muted">{t('contact.sentText', { email: email.trim() })}</p>
        <Link to="/" className="rounded-full bg-ink px-6 py-3 font-extrabold text-on-ink no-underline">
          {t('common.backHome')}
        </Link>
      </main>
    )
  }

  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-6 px-4 pt-10 pb-[72px] sm:px-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-[clamp(32px,4vw,44px)] font-black tracking-[-1px]">{t('contact.title')}</h1>
        <p className="text-lg text-muted">{t('contact.lead')}</p>
      </div>
      <form onSubmit={submit} className="flex flex-col gap-5 rounded-3xl border border-line bg-surface p-6 sm:p-8">
        <fieldset className="m-0 flex flex-col gap-2.5 border-0 p-0">
          <legend className="pb-2.5 text-sm font-extrabold">{t('contact.topic')}</legend>
          <div className="flex flex-wrap gap-2">
            {TOPICS.map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={topic === k}
                onClick={() => setTopic(k)}
                className={`cursor-pointer rounded-full px-4 py-2 text-[15px] transition ${
                  topic === k ? 'bg-ink font-extrabold text-on-ink' : 'border-[1.5px] border-edge font-bold text-ink hover:border-ink'
                }`}
              >
                {t(`contact.topics.${k}`)}
              </button>
            ))}
          </div>
        </fieldset>
        <label className="flex flex-col gap-1.5 text-sm font-extrabold">
          {t('contact.email')}
          <input type="email" required autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputClass} h-[50px]`} />
          <span className="font-medium text-subtle">{t('contact.emailHint')}</span>
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-extrabold">
          {t('contact.name')}
          <input autoComplete="name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} className={`${inputClass} h-[50px]`} />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-extrabold">
          {t('contact.message')}
          <textarea
            required
            minLength={10}
            maxLength={4000}
            rows={7}
            placeholder={t(`contact.placeholders.${topic}`)}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className={`${inputClass} resize-y py-3`}
          />
        </label>
        {/* Bots fill every field; people never see this one. */}
        <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={website} onChange={(e) => setWebsite(e.target.value)} className="absolute -left-[9999px] h-px w-px opacity-0" />
        {state.error ? <p role="alert" className="m-0 text-center font-bold text-live">{state.error}</p> : null}
        <button type="submit" disabled={state.busy} className="h-[54px] cursor-pointer rounded-[14px] bg-teal text-[17px] font-extrabold text-on-teal transition hover:bg-teal-dark disabled:opacity-60">
          {t('contact.send')}
        </button>
      </form>
    </main>
  )
}
